import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import ProcurementService from '@/api/procurement.service';

const CompareQuotations: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [quotations, setQuotations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState<number | null>(null);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    const ids = searchParams.get('ids')?.split(',').map(id => parseInt(id)) || [];
    if (ids.length > 0) {
      fetchQuotations(ids);
    }
  }, [searchParams]);

  const fetchQuotations = async (ids: number[]) => {
    try {
      setIsLoading(true);
      const promises = ids.map(id => ProcurementService.getSupplierQuotation(id.toString()));
      const responses = await Promise.all(promises);
      
      const quotationData = responses
        .filter(response => response.data?.success)
        .map(response => response.data.data);
      
      setQuotations(quotationData);
    } catch (error: any) {
      console.error('Error fetching quotations:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load quotations for comparison",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getLowestPrice = (itemName: string) => {
    const prices = quotations
      .map(q => q.items?.find((item: any) => item.itemName === itemName)?.unitPrice || 0)
      .filter(price => price > 0);
    return Math.min(...prices);
  };

  const getHighestPrice = (itemName: string) => {
    const prices = quotations
      .map(q => q.items?.find((item: any) => item.itemName === itemName)?.unitPrice || 0)
      .filter(price => price > 0);
    return Math.max(...prices);
  };

  const getAllItems = () => {
    const itemsSet = new Set<string>();
    quotations.forEach(quotation => {
      quotation.items?.forEach((item: any) => {
        itemsSet.add(item.itemName);
      });
    });
    return Array.from(itemsSet);
  };

  const handleSelectQuotation = (quotationId: number) => {
    setSelectedQuotation(quotationId);
  };

  const handleCreatePO = () => {
    if (!selectedQuotation) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please select a quotation to create purchase order",
      });
      return;
    }

    const selectedQuote = quotations.find(q => q.id === selectedQuotation);
    navigate('/procurement/purchase-orders/create', {
      state: {
        fromQuotation: selectedQuote
      }
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span>Loading quotations...</span>
        </div>
      </div>
    );
  }

  if (quotations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">No Quotations to Compare</h3>
          <p className="text-muted-foreground">No valid quotations found for comparison.</p>
          <Button 
            onClick={() => navigate('/procurement/quotations')}
            className="mt-4"
          >
            Back to Quotations
          </Button>
        </div>
      </div>
    );
  }

  const allItems = getAllItems();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/procurement/quotations')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Quotations
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleCreatePO}
            disabled={!selectedQuotation}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Create Purchase Order
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Compare Quotations</h1>
        <p className="text-muted-foreground">
          Compare {quotations.length} quotations side by side
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Quotations</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{quotations.length}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Lowest Total</CardTitle>
            <TrendingDown className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(Math.min(...quotations.map(q => q.totalAmount || 0)))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Highest Total</CardTitle>
            <TrendingUp className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(Math.max(...quotations.map(q => q.totalAmount || 0)))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quotation Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Select Quotation</CardTitle>
          <CardDescription>Choose the quotation you want to proceed with</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {quotations.map((quotation) => (
              <div
                key={quotation.id}
                className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                  selectedQuotation === quotation.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                }`}
                onClick={() => handleSelectQuotation(quotation.id)}
              >
                <div className="flex items-center space-x-2 mb-3">
                  <Checkbox
                    checked={selectedQuotation === quotation.id}
                    onChange={() => handleSelectQuotation(quotation.id)}
                  />
                  <h3 className="font-semibold">{quotation.supplier?.name}</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Total Amount:</span>
                    <span className="font-medium">
                      {quotation.totalAmount ? formatCurrency(quotation.totalAmount) : 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Items:</span>
                    <span className="font-medium">{quotation.items?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Due Date:</span>
                    <span className="font-medium">
                      {quotation.dueDate ? new Date(quotation.dueDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Detailed Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Item-by-Item Comparison</CardTitle>
          <CardDescription>Compare prices and terms for each item</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  {quotations.map((quotation) => (
                    <TableHead key={quotation.id} className="text-center">
                      <div>
                        <div className="font-medium">{quotation.supplier?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {quotation.quotationNumber}
                        </div>
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {allItems.map((itemName) => {
                  const lowestPrice = getLowestPrice(itemName);
                  const highestPrice = getHighestPrice(itemName);
                  
                  return (
                    <TableRow key={itemName}>
                      <TableCell className="font-medium">{itemName}</TableCell>
                      {quotations.map((quotation) => {
                        const item = quotation.items?.find((item: any) => item.itemName === itemName);
                        const price = item?.unitPrice || 0;
                        const isLowest = price === lowestPrice && price > 0;
                        const isHighest = price === highestPrice && price > 0 && lowestPrice !== highestPrice;
                        
                        return (
                          <TableCell key={quotation.id} className="text-center">
                            {item ? (
                              <div className="space-y-1">
                                <div className={`font-medium ${isLowest ? 'text-green-600' : isHighest ? 'text-red-600' : ''}`}>
                                  {formatCurrency(price)}
                                  {isLowest && <TrendingDown className="inline h-3 w-3 ml-1" />}
                                  {isHighest && <TrendingUp className="inline h-3 w-3 ml-1" />}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Qty: {item.quantity}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Total: {formatCurrency(price * item.quantity)}
                                </div>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
                <TableRow className="border-t-2">
                  <TableCell className="font-bold">Total Amount</TableCell>
                  {quotations.map((quotation) => {
                    const total = quotation.totalAmount || 0;
                    const lowestTotal = Math.min(...quotations.map(q => q.totalAmount || 0));
                    const isLowest = total === lowestTotal && total > 0;
                    
                    return (
                      <TableCell key={quotation.id} className="text-center">
                        <div className={`text-lg font-bold ${isLowest ? 'text-green-600' : ''}`}>
                          {formatCurrency(total)}
                          {isLowest && <TrendingDown className="inline h-4 w-4 ml-1" />}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CompareQuotations; 